import {
  ColDef,
  ColGroupDef,
  Column,
  ICellRendererParams,
  IHeaderGroupParams,
} from '@ag-grid-community/core';
import dayjs from 'dayjs';

import {
  AgColumnGroupHeaderComponent,
  AgColumnGroupHeaderExternalParams,
} from '@shared/ag-components/ag-column-group-header/ag-column-group-header';
import { AgRawTooltipParams } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { AuxIconName } from '@shared/components/icon/icon';
import { AuxExcelStyleKeys } from '@shared/utils/aux-excel-styles/aux-excel-style-keys';

import { QCGridData } from '../../../../period-close.model';
import {
  QCMonthEstimateCellRendererComponent,
  QCMonthEstimateCellRendererParams,
} from '../qc-month-wp-estimate-cell-renderer.component';
import {
  qcGetFluxAnalysisColumns,
  qcGetFluxPrevValueColumn,
  qcGetInvoiceColumnDefaults,
  qcGetMoneyColumn,
  qcGetUnpaidInvoicesCellRenderer,
  QuarterCloseGridConfigContext,
  QuarterCloseMonthColumnConfig,
} from './qc-grid-config-context';
import {
  AgQuarterCloseCollapsableHeaderComponent,
  AgQuarterCloseCollapsableHeaderParams,
} from '../ag-quarter-close-collapsable-header.component';

const localStorageKey = 'closing_page_qc_month';

export const qcMonthColumnGroup = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  isFluxAnalysisAvailableAndOn: boolean
): ColGroupDef => {
  let headerName = `${dayjs(tmp.month).format('MMMM YYYY')}`;

  if (tmp.isAfter) {
    headerName += ' (Closed)';
  } else if (tmp.isCurrent) {
    headerName += ' (Open)';
  }

  const openByDefault = localStorage.getItem(`${localStorageKey}_${tmp.index}`) === 'true';

  const debitColumns = getDebitCreditColumns(ctx, tmp);

  return <ColGroupDef>{
    children: getChildren(ctx, tmp, isFluxAnalysisAvailableAndOn),
    headerClass: [
      'text-center',
      'header-marker',
      'blue-light-marker',
      tmp.index === 1 ? AuxExcelStyleKeys.ALTERNATE : '',
    ],
    headerGroupComponent: AgColumnGroupHeaderComponent,
    headerGroupComponentParams: <AgColumnGroupHeaderExternalParams>{
      onToggle: (isOpen: boolean, params: IHeaderGroupParams) => {
        localStorage.setItem(localStorageKey, String(isOpen));
        params.api.refreshCells({
          columns: (params.columnGroup.getDisplayedChildren() || []) as Column[],
          force: true,
        });

        if (!isOpen) {
          debitColumns.forEach((column) => {
            params.api.setColumnVisible(column.field!, false);
          });
        }
      },
    },
    headerName,
    openByDefault,
  };
};

const getChildren = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  isFluxAnalysisAvailableAndOn: boolean
): ColDef[] => {
  const hideByDefault = isFluxAnalysisAvailableAndOn && !tmp.isCurrent;

  return [
    ...qcGetFluxPrevValueColumn(
      'previous_month_wp_estimate',
      'Previous Month Monthly Expense',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    getMonthlyWPEstimateColumn(ctx, tmp, hideByDefault),
    ...qcGetFluxAnalysisColumns(
      'monthly_expense_variance',
      'Monthly Expense MoM Var ($)',
      'monthly_expense_variance_percentage',
      'Monthly Expense MoM Var (%)',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    ...qcGetFluxPrevValueColumn(
      'previous_month_invoices_received',
      'Previous Month Monthly Invoice',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    getMonthlyInvoicesReceivedColumn(ctx, tmp, hideByDefault),
    ...qcGetFluxAnalysisColumns(
      'monthly_invoices_variance',
      'Monthly Invoices MoM Var ($)',
      'monthly_invoices_variance_percentage',
      'Monthly Invoices MoM Var (%)',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    ...getNetChangeAccrualsColumns(ctx, tmp, isFluxAnalysisAvailableAndOn),
    getExpenseLTDColumn(tmp, hideByDefault),
    getInvoicedLTDColumn(ctx, tmp, hideByDefault),
    ...qcGetFluxPrevValueColumn(
      'previous_month_accrual_ltd',
      'Previous Month Accrual LTD',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    ...qcGetFluxPrevValueColumn(
      'previous_month_prepaid_ltd',
      'Previous Month Prepaid LTD',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    getAccrualLTDColumn(tmp, hideByDefault),
    getPrepaidLTDColumn(tmp, hideByDefault),
    ...qcGetFluxAnalysisColumns(
      'monthly_accrual_prepaid_difference',
      'Accrual/Prepaid LTD MoM Var ($)',
      'monthly_accrual_prepaid_difference_percentage',
      'Accrual/Prepaid LTD MoM Var (%)',
      tmp.isCurrent,
      !isFluxAnalysisAvailableAndOn
    ),
    ...getUnpaidInvoicesColumn(ctx, tmp, isFluxAnalysisAvailableAndOn),
    ...getAdvancePaymentsColumns(ctx, tmp, isFluxAnalysisAvailableAndOn),
  ];
};

const getMonthlyWPEstimateColumn = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef => {
  const { adjustmentWorkflow, isWorkflowAvailable } = ctx;

  let iconName: AuxIconName = 'Eye';

  if (isWorkflowAvailable && adjustmentWorkflow) {
    if (
      !adjustmentWorkflow.properties.locked &&
      ctx.permissions.canModifyAdjustments &&
      tmp.isCurrent
    ) {
      iconName = 'Pencil';
    }
  } else {
    if (tmp.isCurrent) {
      iconName = 'Pencil';
    }
  }

  const isDisabled: (params: ICellRendererParams<QCGridData>) => boolean = (params) => {
    if (!ctx.flags.isMonthTabAvailable) {
      return true;
    }

    return (
      tmp.isCurrent &&
      ctx.permissions.canModifyAdjustments &&
      adjustmentWorkflow?.properties.locked === false &&
      params.data?.cost_category === 'Discount'
    );
  };

  return {
    ...qcGetMoneyColumn(),
    cellRenderer: QCMonthEstimateCellRendererComponent,
    cellRendererParams: <QCMonthEstimateCellRendererParams>{
      iconName,
      isDisabled,
      onClicked: ctx.onMonthlyWpEstimateCellClicked,
    },
    columnGroupShow: 'open',
    field: `qmonth${tmp.index}_monthly_wp_estimate`,
    headerClass: ['text-center', 'whitespace-normal'],
    headerName: `Monthly Expense${tmp.isAfter ? '' : ' Estimate'}`,
    hide: hideByDefault,
    tooltipComponentParams: <AgRawTooltipParams>{
      getNode: ({ targetNode }) => targetNode?.querySelector('div'),
    },
  };
};

const getMonthlyInvoicesReceivedColumn = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef => {
  return {
    ...qcGetMoneyColumn(),
    ...qcGetInvoiceColumnDefaults(ctx),
    columnGroupShow: 'open',
    field: `qmonth${tmp.index}_monthly_invoices_received`,
    headerClass: ['text-center', 'whitespace-normal'],
    headerName: 'Monthly Invoices Received',
    hide: hideByDefault,
  };
};

const getNetChangeAccrualsColumns = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef[] => {
  const debitCreditColumns = getDebitCreditColumns(ctx, tmp);

  return [
    {
      ...qcGetMoneyColumn(),
      columnGroupShow: 'open',
      field: `qmonth${tmp.index}_net_change_in_accruals`,
      headerClass: ['text-center', 'font-semibold'],

      headerComponent: AgQuarterCloseCollapsableHeaderComponent,
      headerComponentParams: <AgQuarterCloseCollapsableHeaderParams>{
        columns: debitCreditColumns.map((column) => column.field),
        template: `Net Change in <br/> Accruals / (Prepaid)`,
      },
      headerName: 'Net Change in Accruals / (Prepaid)',
      hide: hideByDefault,
      maxWidth: 210,
    },
    ...debitCreditColumns,
  ];
};

const getDebitCreditColumns = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig
): ColDef[] => {
  return [
    {
      ...qcGetMoneyColumn(),
      field: `qmonth${tmp.index}_accrual_debit`,
      headerName: 'Accrual Debit',
      hide: true,
    },
    {
      ...qcGetMoneyColumn(),
      field: `qmonth${tmp.index}_accrual_credit`,
      headerName: 'Accrual Credit',
      hide: true,
    },
    {
      ...qcGetMoneyColumn(),
      field: `qmonth${tmp.index}_prepaid_debit`,
      headerName: 'Prepaid Debit',
      hide: true,
    },
    {
      ...qcGetMoneyColumn(),
      field: `qmonth${tmp.index}_prepaid_credit`,
      headerName: 'Prepaid Credit',
      hide: true,
    },
  ];
};

const getExpenseLTDColumn = (
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef => {
  return {
    ...qcGetMoneyColumn(),
    columnGroupShow: 'open',
    field: `qmonth${tmp.index}_wp_ltd`,
    headerName: 'Expense LTD',
    hide: hideByDefault,
  };
};

const getInvoicedLTDColumn = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef => {
  return {
    ...qcGetMoneyColumn(),
    ...qcGetInvoiceColumnDefaults(ctx),
    columnGroupShow: 'open',
    field: `qmonth${tmp.index}_invoiced_ltd`,
    headerName: 'Invoiced LTD',
    hide: hideByDefault,
  };
};

const getAccrualLTDColumn = (
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef => {
  return {
    ...qcGetMoneyColumn(),
    field: `qmonth${tmp.index}_accrual_ltd`,
    headerName: 'Accrual LTD',
    hide: hideByDefault,
    maxWidth: undefined,
    minWidth: tmp.isCurrent ? 175 : 125,
  };
};

const getPrepaidLTDColumn = (
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef => {
  return {
    ...qcGetMoneyColumn(),
    field: `qmonth${tmp.index}_prepaid_ltd`,
    headerName: 'Prepaid LTD',
    hide: hideByDefault,
    maxWidth: undefined,
    minWidth: tmp.isCurrent ? 175 : 125,
  };
};

const getUnpaidInvoicesColumn = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef[] => {
  const columns: ColDef[] = [];

  if (ctx.flags.isUnpaidColumnsVisible) {
    columns.push({
      ...qcGetMoneyColumn(),
      ...qcGetInvoiceColumnDefaults(ctx),
      columnGroupShow: 'open',
      field: `qmonth${tmp.index}_unpaid_invoices_ltd`,
      headerName: 'Unpaid Invoices LTD',
      hide: hideByDefault,
      ...qcGetUnpaidInvoicesCellRenderer(),
    });
  }

  return columns;
};

const getAdvancePaymentsColumns = (
  ctx: QuarterCloseGridConfigContext,
  tmp: QuarterCloseMonthColumnConfig,
  hideByDefault: boolean
): ColDef[] => {
  const columns: ColDef[] = [];

  if (ctx.flags.isAPColumnVisible) {
    columns.push({
      ...qcGetMoneyColumn(),
      columnGroupShow: 'open',

      field: `qmonth${tmp.index}_advanced_payments_ltd`,
      headerName: 'Advanced Payments LTD',
      hide: hideByDefault,
    });
  }

  if (ctx.flags.isNPColumnVisible) {
    columns.push({
      ...qcGetMoneyColumn(),
      columnGroupShow: 'open',

      field: `qmonth${tmp.index}_net_position`,
      headerName: 'Net Position',
      hide: hideByDefault,
    });
  }

  return columns;
};
